// Shared display helpers for the unified Goals module (board + detail page).
// Keeping these in one place means the Weekly/Monthly board and the goal detail
// page render priorities, health, key results and progress identically.
import { DateTime } from 'luxon'
import { formatDateOnly } from './format'

export const PRIORITY_LABEL = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' }
export const PRIORITY_PILL = {
  urgent: 'bg-[color:var(--color-priority-urgent-bg,#fee2e2)] text-[color:var(--color-priority-urgent)]',
  high: 'bg-[color:var(--color-priority-high-bg,#fef3c7)] text-[color:var(--color-priority-high)]',
  normal: 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]',
  low: 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-subtle)]',
}

export const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'carried_forward', label: 'Carried Forward' },
]

// Goal status pill — deliberately the SAME due-time vocabulary + colours Tasks
// use (Overdue = red, Due today = amber, Completed = green), so a goal reads
// consistently with a task. Derived server-side by `deriveHealth` from the
// target date. Legacy values (on_track/at_risk/off_track) are kept mapped so any
// cached/older payload still renders a sensible label instead of a raw key.
export const HEALTH_LABEL = {
  overdue: 'Overdue',
  due_today: 'Due today',
  in_progress: 'In Progress',
  completed: 'Completed',
  // Explicit manual statuses surfaced by `deriveHealth` so the board card reads
  // the same status the goal detail shows.
  not_started: 'Not Started',
  on_hold: 'On Hold',
  carried_forward: 'Carried Forward',
  // legacy fallbacks
  on_track: 'In Progress',
  at_risk: 'Due today',
  off_track: 'Overdue',
}
export const HEALTH_PILL = {
  overdue: 'bg-[color:var(--color-priority-urgent-bg,#fee2e2)] text-[color:var(--color-priority-urgent)]',
  due_today: 'bg-[color:var(--color-priority-high-bg,#fef3c7)] text-[color:var(--color-priority-high)]',
  // Blue — the same token Tasks use for their "In Progress" workflow state.
  in_progress: 'bg-[color:var(--color-status-progress,#3b82f6)]/15 text-[color:var(--color-status-progress,#3b82f6)]',
  completed: 'bg-[color:var(--color-status-complete,#16a34a)]/15 text-[color:var(--color-status-complete,#16a34a)]',
  // Manual statuses — neutral/muted so they read as "state", not urgency.
  not_started: 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]',
  on_hold: 'bg-[color:var(--color-priority-high-bg,#fef3c7)] text-[color:var(--color-priority-high)]',
  carried_forward: 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-subtle)]',
  // legacy fallbacks
  on_track: 'bg-[color:var(--color-status-progress,#3b82f6)]/15 text-[color:var(--color-status-progress,#3b82f6)]',
  at_risk: 'bg-[color:var(--color-priority-high-bg,#fef3c7)] text-[color:var(--color-priority-high)]',
  off_track: 'bg-[color:var(--color-priority-urgent-bg,#fee2e2)] text-[color:var(--color-priority-urgent)]',
}

// Key-result types offered in the Add / Edit Goal form. Currency is
// intentionally NOT offered per product request — only Number and Percentage
// key results can be added. `TARGET_LABEL` below still maps `currency` so any
// legacy key result created before this change keeps rendering its badge
// correctly (display stays non-breaking; the type just can't be added anymore).
export const TARGET_TYPES = [
  { key: 'number', label: 'Number', hint: 'Any number, e.g. 50' },
  { key: 'percentage', label: 'Percentage', hint: '0–100%' },
]
export const TARGET_LABEL = { number: 'Number', percentage: 'Percentage', currency: 'Currency' }

export function newKeyResult(type) {
  if (type === 'percentage') return { type, name: '', start: 0, target: 100, current: 0 }
  return { type, name: '', start: 0, target: '', current: 0 }
}
export function formatKrValue(t) {
  const cur = t.current ?? 0
  const tgt = t.target ?? (t.type === 'percentage' ? 100 : 0)
  const suffix = t.type === 'percentage' ? '%' : ''
  return `${cur}${suffix} / ${tgt}${suffix}`
}

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** Vivid per-goal accent (top border of a card). Stable per goal id, so a card
 *  keeps its colour across renders. Chosen to read on light + dark surfaces. */
export const GOAL_ACCENTS = [
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#22c55e', // green
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#06b6d4', // cyan
  '#f43f5e', // rose
  '#fb923c', // orange
]
export function goalAccent(id) {
  const s = String(id ?? '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return GOAL_ACCENTS[h % GOAL_ACCENTS.length]
}

/** "1 day ago" style relative time from an ISO string (Luxon-backed). */
export function timeAgo(iso) {
  if (!iso) return ''
  const dt = DateTime.fromISO(iso)
  if (!dt.isValid) return ''
  return dt.toRelative() ?? ''
}

/** 'YYYY-MM-DD' → the person's date format (default 'DD/MM/YYYY'). The
 *  shared `formatDateOnly` treats a date-only string as a calendar day,
 *  so the day never shifts with the timezone. */
export function fmtDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
  return formatDateOnly(iso)
}
// `fmtDateTime` (a bespoke 24-hour/comma/browser-local formatter) was removed —
// date-time instants must use the canonical `formatDateTime` from lib/format
// (DD/MM/YYYY HH:MM AM/PM, rendered in the display timezone) so timestamps stay
// consistent across the app. `fmtDate` above formats DATE-ONLY
// 'YYYY-MM-DD' values via `formatDateOnly`, which reads them as calendar days
// (no timezone shift) in the person's date format.
export function addDays(iso, days) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}
/** First day of the month `delta` months from `iso`. */
export function monthShift(iso, delta) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const [y, m] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 10)
}

/** Circular progress ring with the percent in the centre. `barColor` /
 *  `trackColor` / `textClass` let a coloured hero render a white-on-colour ring. */
export function ProgressRing({ percent, size = 46, stroke = 5, barColor, trackColor, textClass }) {
  const pct = Math.max(0, Math.min(100, Math.round(percent ?? 0)))
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const offset = c - (pct / 100) * c
  const color = barColor ?? (pct >= 100 ? 'var(--color-status-complete,#16a34a)' : 'var(--color-accent-strong)')
  const fontSize = Math.max(9, Math.round(size / 4.2))
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor ?? 'var(--color-card-hover)'} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <span className={'absolute font-bold ' + (textClass ?? 'text-[color:var(--color-ink-strong)]')} style={{ fontSize }}>
        {pct}%
      </span>
    </span>
  )
}
